import React, { useState, useRef, useEffect } from 'react';

interface TooltipProps {
  content: string;
  children: React.ReactElement;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  disabled?: boolean;
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = 'top',
  delay = 300,
  disabled = false,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const showTooltip = () => {
    if (disabled || !content) return;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const getTooltipStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      position: 'absolute',
      zIndex: 9999,
      padding: '4px 8px',
      fontSize: '12px',
      fontWeight: 500,
      color: 'white',
      backgroundColor: '#1f2937',
      borderRadius: '4px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      whiteSpace: 'nowrap',
      pointerEvents: 'none' as const,
      transition: 'opacity 0.1s ease-in-out, transform 0.1s ease-in-out',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'scale(1)' : 'scale(0.95)',
    };

    switch (position) {
      case 'top':
        return {
          ...baseStyle,
          bottom: 'calc(100% + 8px)',
          left: '50%',
          transform: `translateX(-50%) ${isVisible ? 'scale(1)' : 'scale(0.95)'}`,
        };
      case 'bottom':
        return {
          ...baseStyle,
          top: 'calc(100% + 8px)',
          left: '50%',
          transform: `translateX(-50%) ${isVisible ? 'scale(1)' : 'scale(0.95)'}`,
        };
      case 'left':
        return {
          ...baseStyle,
          right: 'calc(100% + 8px)',
          top: '50%',
          transform: `translateY(-50%) ${isVisible ? 'scale(1)' : 'scale(0.95)'}`,
        };
      case 'right':
        return {
          ...baseStyle,
          left: 'calc(100% + 8px)',
          top: '50%',
          transform: `translateY(-50%) ${isVisible ? 'scale(1)' : 'scale(0.95)'}`,
        };
      default:
        return {
          ...baseStyle,
          bottom: 'calc(100% + 8px)',
          left: '50%',
          transform: `translateX(-50%) ${isVisible ? 'scale(1)' : 'scale(0.95)'}`,
        };
    }
  };

  const getArrowStyle = (): React.CSSProperties => {
    const baseArrowStyle: React.CSSProperties = {
      position: 'absolute',
      width: 0,
      height: 0,
      border: '4px solid transparent',
      pointerEvents: 'none' as const,
      transition: 'opacity 0.1s ease-in-out, transform 0.1s ease-in-out',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'scale(1)' : 'scale(0.95)',
    };

    switch (position) {
      case 'top':
        return {
          ...baseArrowStyle,
          top: '100%',
          left: '50%',
          transform: `translateX(-50%) ${isVisible ? 'scale(1)' : 'scale(0.95)'}`,
          borderTopColor: '#1f2937',
          borderBottomWidth: 0,
        };
      case 'bottom':
        return {
          ...baseArrowStyle,
          bottom: '100%',
          left: '50%',
          transform: `translateX(-50%) ${isVisible ? 'scale(1)' : 'scale(0.95)'}`,
          borderBottomColor: '#1f2937',
          borderTopWidth: 0,
        };
      case 'left':
        return {
          ...baseArrowStyle,
          left: '100%',
          top: '50%',
          transform: `translateY(-50%) ${isVisible ? 'scale(1)' : 'scale(0.95)'}`,
          borderLeftColor: '#1f2937',
          borderRightWidth: 0,
        };
      case 'right':
        return {
          ...baseArrowStyle,
          right: '100%',
          top: '50%',
          transform: `translateY(-50%) ${isVisible ? 'scale(1)' : 'scale(0.95)'}`,
          borderRightColor: '#1f2937',
          borderLeftWidth: 0,
        };
      default:
        return {
          ...baseArrowStyle,
          top: '100%',
          left: '50%',
          transform: `translateX(-50%) ${isVisible ? 'scale(1)' : 'scale(0.95)'}`,
          borderTopColor: '#1f2937',
          borderBottomWidth: 0,
        };
    }
  };

  // Clone the child element and add event handlers
  const childWithHandlers = React.cloneElement(children, {
    onMouseEnter: (e: React.MouseEvent) => {
      showTooltip();
      children.props.onMouseEnter?.(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      hideTooltip();
      children.props.onMouseLeave?.(e);
    },
    onFocus: (e: React.FocusEvent) => {
      showTooltip();
      children.props.onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent) => {
      hideTooltip();
      children.props.onBlur?.(e);
    },
  });

  return (
    <span className={`relative ${className}`} style={{ display: 'inline-block' }}>
      {childWithHandlers}
      {(isVisible || timeoutRef.current) && (
        <div
          style={getTooltipStyle()}
          role="tooltip"
          aria-hidden={!isVisible}
        >
          {content}
          <div style={getArrowStyle()} />
        </div>
      )}
    </span>
  );
};